import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const AmbientAudioContext = createContext(null);

export const AmbientAudioProvider = ({ children }) => {
  const { pathname } = useLocation();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const userMutedRef = useRef(false);
  const wasPlayingBeforeHiddenRef = useRef(false);
  const fadeIntervalRef = useRef(null);

  const fadeUpVolume = () => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    let vol = audio.volume || 0;
    fadeIntervalRef.current = setInterval(() => {
      vol = Math.min(0.35, vol + 0.07);
      if (audio) audio.volume = vol;
      if (vol >= 0.35) {
        clearInterval(fadeIntervalRef.current);
      }
    }, 50);
  };

  const startMusic = () => {
    if (!audioRef.current || userMutedRef.current) return;

    // If tab is not visible (opened in background or switched away), defer play until tab is active
    if (typeof document !== "undefined" && document.hidden) {
      wasPlayingBeforeHiddenRef.current = true;
      return;
    }

    const audio = audioRef.current;

    audio.muted = false;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
          setIsMuted(false);
          fadeUpVolume();
        })
        .catch((err) => {
          console.warn("Meditation music autoplay deferred until user interaction:", err.message);
        });
    } else {
      setIsPlaying(true);
      setIsMuted(false);
      fadeUpVolume();
    }
  };

  useEffect(() => {
    const audio = new Audio("/audio/Healing_Sleep.mp3");
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0;
    audio.muted = false;
    audioRef.current = audio;

    const isInnerPage = pathname !== "/";
    const doorsAlreadyOpen = window.__veeduDoorsOpened === true;

    const handleDoorsOpened = () => {
      startMusic();
    };

    if (isInnerPage || doorsAlreadyOpen) {
      startMusic();
    } else {
      window.addEventListener("veedu:doors-opened", handleDoorsOpened, { once: true });
    }

    // Attempt immediate unmuted play on page load (works when typing URL & pressing Enter on desktop/supported browsers)
    startMusic();

    // Passive unlock listener for mobile browsers where autoplay policy defers playback until user touch/click
    const handlePassiveUnlock = () => {
      if (!userMutedRef.current) {
        startMusic();
      }
    };

    window.addEventListener("touchstart", handlePassiveUnlock, { capture: true, once: true });
    window.addEventListener("pointerdown", handlePassiveUnlock, { capture: true, once: true });
    window.addEventListener("click", handlePassiveUnlock, { capture: true, once: true });

    // Pause audio when user switches to another tab or minimizes browser, and resume when returning
    const handleVisibilityChange = () => {
      if (!audioRef.current) return;
      const audio = audioRef.current;

      if (document.hidden) {
        // Tab is hidden: pause immediately so no audio leaks to background
        if (!audio.paused && !userMutedRef.current) {
          wasPlayingBeforeHiddenRef.current = true;
          if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
          audio.pause();
          setIsPlaying(false);
        }
      } else {
        // User switched back to this tab: resume if it was active before and not explicitly muted
        if (wasPlayingBeforeHiddenRef.current && !userMutedRef.current) {
          wasPlayingBeforeHiddenRef.current = false;
          audio.muted = false;
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
                setIsMuted(false);
                fadeUpVolume();
              })
              .catch((err) => {
                console.warn("Resume audio deferred until user interaction:", err.message);
              });
          } else {
            setIsPlaying(true);
            setIsMuted(false);
            fadeUpVolume();
          }
        }
      }
    };

    const handlePageHide = () => {
      if (!audioRef.current) return;
      const audio = audioRef.current;
      if (!audio.paused && !userMutedRef.current) {
        wasPlayingBeforeHiddenRef.current = true;
        if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
        audio.pause();
        setIsPlaying(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("veedu:doors-opened", handleDoorsOpened);
      window.removeEventListener("touchstart", handlePassiveUnlock, { capture: true });
      window.removeEventListener("pointerdown", handlePassiveUnlock, { capture: true });
      window.removeEventListener("click", handlePassiveUnlock, { capture: true });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMute = () => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    if (audio.paused || isMuted) {
      userMutedRef.current = false;
      wasPlayingBeforeHiddenRef.current = false;
      audio.muted = false;
      audio.play().then(() => {
        audio.volume = 0.35;
        setIsPlaying(true);
        setIsMuted(false);
      }).catch(() => {
        audio.volume = 0.35;
        setIsPlaying(true);
        setIsMuted(false);
      });
      return;
    }

    userMutedRef.current = true;
    wasPlayingBeforeHiddenRef.current = false;
    audio.muted = true;
    audio.pause();
    setIsMuted(true);
    setIsPlaying(false);
  };

  return (
    <AmbientAudioContext.Provider value={{ isPlaying, isMuted, toggleMute, startMusic }}>
      {children}
    </AmbientAudioContext.Provider>
  );
};

export const useAmbientAudio = () => {
  const context = useContext(AmbientAudioContext);
  if (!context) {
    return { isPlaying: false, isMuted: false, toggleMute: () => {}, startMusic: () => {} };
  }
  return context;
};

export default AmbientAudioContext;

